import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Segmented } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@argo/common/auth'
import { config } from '@argo/common/config'
import { useTheme, type Mode } from '@argo/common/theme'
import ContactPanel from '@argo/common/components/ContactPanel'
import { api } from '../api'
import { setLanguage } from '../i18n'
import { useSession } from '../session'

/**
 * The account, its contact details and its PIN — all shared with the exchange,
 * because they are the same account. Changing the PIN here changes the one
 * that authorises a trade there.
 */
export default function Settings({ before }: { before?: ReactNode } = {}) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const { me, account } = useSession()
  const { mode, setMode } = useTheme()
  const { signOut } = useAuth()

  const pinStatus = useQuery({ queryKey: ['pin'], queryFn: api.pinStatus })
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [note, setNote] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)

  const setPin = useMutation({
    mutationFn: () => api.setPin(next, current || undefined),
    onSuccess: () => {
      setNote({ text: t('done'), kind: 'ok' })
      setCurrent(''); setNext('')
      qc.invalidateQueries({ queryKey: ['pin'] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e: Error) => setNote({ text: e.message, kind: 'err' }),
  })

  const submitPin = (e: FormEvent) => {
    e.preventDefault()
    setNote(null)
    if (!/^\d{4,8}$/.test(next)) { setNote({ text: t('pinHint'), kind: 'err' }); return }
    setPin.mutate()
  }

  const lang = i18n.language.startsWith('mn') ? 'mn' : 'en'

  return (
    <div className="page page-mid cols cols-2">
      {before}
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('account')}</span></div>
        <div className="panel-body flush">
          <div className="list-row">
            <div className="grow">
              <div className="title">{me?.username || '—'}</div>
              <div className="sub">{t('username')}</div>
            </div>
            <div className="row" style={{ gap: 4 }}>{(me?.roles ?? []).map(r => <span key={r} className="chip">{r}</span>)}</div>
          </div>
          <div className="list-row">
            <div className="grow">
              <div className="title">{account || '—'}</div>
              <div className="sub">{t('account')}</div>
            </div>
          </div>
          {config.auth !== 'off' && (
            <div className="list-row">
              <div className="grow"><div className="sub">{t('signOut')}</div></div>
              <button className="btn-ghost" onClick={signOut}>{t('signOut')}</button>
            </div>
          )}
        </div>
      </div>

      <ContactPanel />

      <form className="panel" onSubmit={submitPin}>
        <div className="panel-head">
          <span className="grow">{t('security')}</span>
          <span className="chip">{pinStatus.data?.set ? t('pinSet') : t('pinNotSet')}</span>
        </div>
        <div className="panel-body">
          {pinStatus.data?.set && (
            <div className="field">
              <label htmlFor="current">{t('currentPin')}</label>
              <div className="input-wrap">
                <input id="current" type="password" inputMode="numeric" autoComplete="current-password"
                  value={current} onChange={e => setCurrent(e.target.value)} />
              </div>
            </div>
          )}
          <div className="field">
            <label htmlFor="next">{t('newPin')}</label>
            <div className="input-wrap">
              <input id="next" type="password" inputMode="numeric" autoComplete="new-password"
                value={next} onChange={e => setNext(e.target.value)} />
            </div>
          </div>
          <p className="field-hint">{t('pinHint')}</p>
          {note && <p className={`form-note ${note.kind}`}>{note.text}</p>}
          <button className="submit buy" disabled={setPin.isPending}>{t('save')}</button>
        </div>
      </form>

      <div className="panel">
        <div className="panel-head"><span className="grow">{t('settings')}</span></div>
        <div className="panel-body stack">
          <div className="field">
            <label>{t('language')}</label>
            <Segmented
              value={lang}
              onChange={v => setLanguage(v as 'mn' | 'en')}
              options={[{ label: 'Монгол', value: 'mn' }, { label: 'English', value: 'en' }]}
              block
            />
          </div>
          <div className="field">
            <label>{t('theme')}</label>
            <Segmented
              value={mode}
              onChange={v => setMode(v as Mode)}
              options={[
                { label: t('light'), value: 'light' },
                { label: t('dark'), value: 'dark' },
                { label: t('system'), value: 'system' },
              ]}
              block
            />
          </div>
        </div>
      </div>
    </div>
  )
}
